import { createActionCreators } from 'immer-reducer';

import { HolidaySyncResult, SlackReducer } from '@/store/reducers/slack';
import { revalidate } from '@/store/responses';
import { getUser } from './users';
import { AsyncAction } from './common';

export const slackActions = createActionCreators(SlackReducer);

export type SlackActions =
  | ReturnType<typeof slackActions.setDirectory>
  | ReturnType<typeof slackActions.setHolidays>
  | ReturnType<typeof slackActions.setHolidayStatus>
  | ReturnType<typeof slackActions.cleanHolidays>
  | ReturnType<typeof slackActions.cleanSlack>;

export const getSlackDirectory = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/slack/users',
      () => mainProtectedApi.getSlackDirectory(),
      (directory) => dispatch(slackActions.setDirectory(directory)),
      { slot: 'slackReducer.directory' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getHolidayRecords = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/vacations/holidays?full=1',
      () => mainProtectedApi.getHolidayRecords(),
      (holidays) => dispatch(slackActions.setHolidays(holidays)),
      { slot: 'slackReducer.holidays' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getHolidayStatus = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/vacations/holidays/status',
      () => mainProtectedApi.getHolidayStatus(),
      (status) => dispatch(slackActions.setHolidayStatus(status)),
      { slot: 'slackReducer.holidayStatus' },
    );
  } catch (e) {
    console.log(e);
  }
};

// A calendar edit moves the status too -- coverage per year, and which holiday is
// polled next.
const refreshHolidays = (): AsyncAction => (dispatch) => {
  dispatch(getHolidayRecords());
  dispatch(getHolidayStatus());
};

export type HolidaySyncOutcome =
  | ({ ok: true } & HolidaySyncResult)
  | { ok: false; message?: string };

/**
 * Pull a year from work.ua. Resolves to what happened, so the screen can say it: the
 * back end's reason on a failure is the useful part (work.ua down, markup changed).
 */
export const syncHolidays = (
  year: number,
): AsyncAction<Promise<HolidaySyncOutcome>> => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    // The response interceptor has already unwrapped the body.
    const data = await mainProtectedApi.syncHolidays(year);

    dispatch(refreshHolidays());

    return { ok: true, ...(data as unknown as HolidaySyncResult) };
  } catch (e) {
    // The back end answers an error with its message as plain text.
    const message = (e as { response?: { data?: unknown } }).response?.data;

    return { ok: false, message: typeof message === 'string' && message ? message : undefined };
  }
};

// Every mutation awaits the write before refetching: dispatching the thunk and
// calling the refetch straight after reads the old value back.

export const linkSlackUser = (
  slackUserId: string,
  userId: number,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.linkSlackUser({ slackUserId, userId });

    dispatch(getSlackDirectory());
  } catch (e) {
    console.log(e);
  }
};

export const unlinkSlackUser = (userId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await mainProtectedApi.unlinkSlackUser(userId);

    dispatch(getSlackDirectory());
  } catch (e) {
    console.log(e);
  }
};

/**
 * Bind (or clear) the Slack account of one employee, from their own page.
 *
 * Awaits the write before refetching -- dispatching the thunk and refetching straight
 * after reads the old value back. Both the employee and the directory are refreshed:
 * the row on the page comes from the user, the picker's list of free accounts from the
 * directory.
 */
export const setUserSlackAccount = (
  userId: number,
  slackUserId: string | null,
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    if (slackUserId) {
      await mainProtectedApi.linkSlackUser({ slackUserId, userId });
    } else {
      await mainProtectedApi.unlinkSlackUser(userId);
    }

    dispatch(getUser(userId, true));
    dispatch(getSlackDirectory());
  } catch (e) {
    console.log(e);
  }
};

export interface NewHoliday {
  title: string;
  date: string;
  observedDate?: string;
}

export const addHoliday = (holiday: NewHoliday): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await mainProtectedApi.addHoliday(holiday);

    dispatch(refreshHolidays());
  } catch (e) {
    console.log(e);
  }
};

export const updateHoliday = (
  id: number,
  update: Partial<NewHoliday> & { isCancelled?: boolean },
): AsyncAction => async (dispatch, _, { mainProtectedApi }) => {
  try {
    await mainProtectedApi.updateHoliday(id, update);

    dispatch(refreshHolidays());
  } catch (e) {
    console.log(e);
  }
};

export const deleteHoliday = (id: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await mainProtectedApi.deleteHoliday(id);

    dispatch(refreshHolidays());
  } catch (e) {
    console.log(e);
  }
};
